import React from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Description as DocIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { DocumentFileCard } from "./document-file-card";
import { InlineChecklist, type InlineChecklistData, type CheckItemResult } from "./inline-validation-checklist";

interface DocumentSlotRowProps {
  slot: any;
  sectionId: string;
  slotDocs: any[];
  isLastSlot: boolean;
  uploading: boolean;
  uploadTargetSlotId: string | undefined;
  checklistData: InlineChecklistData | undefined;
  rerunningCheckId: string | null;
  existingRequestSlotIds: Set<string>;
  pendingSlotIds: Set<string>;
  existingRequests: any[];
  onUploadClick: (category: string, slotId: string) => void;
  onViewDoc: (doc: any) => void;
  onDownloadDoc: (doc: any) => void;
  onDeleteDoc: (docId: string) => void;
  onChecklistItemClick: (item: CheckItemResult, checkName: string, reasoning?: string, citations?: any[]) => void;
  onRerun: (checkId: string) => void;
  onRequestRevision: (slotId: string, label: string) => void;
  onRevisionUploadClick: (requestId: string, category: string, slotId: string, reason: string) => void;
  onEditRequest: (request: any) => void;
  onDeleteRequest: (requestId: string) => void;
}

export const DocumentSlotRow: React.FC<DocumentSlotRowProps> = ({
  slot,
  sectionId,
  slotDocs,
  isLastSlot,
  uploading,
  uploadTargetSlotId,
  checklistData,
  rerunningCheckId,
  existingRequestSlotIds,
  pendingSlotIds,
  existingRequests,
  onUploadClick,
  onViewDoc,
  onDownloadDoc,
  onDeleteDoc,
  onChecklistItemClick,
  onRerun,
  onRequestRevision,
  onRevisionUploadClick,
  onEditRequest,
  onDeleteRequest,
}) => {
  const hasDoc = slotDocs.length > 0;

  return (
    <Box
      sx={{
        borderBottom: !isLastSlot ? `1px solid ${tokens.border.light}` : "none",
      }}
    >
      {/* Slot Row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {hasDoc ? (
            <CheckIcon sx={{ color: tokens.status.success, fontSize: 18, flexShrink: 0 }} />
          ) : (
            <DocIcon sx={{ color: tokens.text.disabled, fontSize: 18, flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.primary,
                }}
              >
                {slot.label}
              </Typography>
              {slot.required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "10px",
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: hasDoc ? `${tokens.status.success}12` : `${tokens.status.warning}12`,
                    color: hasDoc ? tokens.status.success : tokens.status.warning,
                    border: "none",
                  }}
                />
              )}
            </Stack>
            <Typography
              sx={{
                fontSize: tokens.fontSize["2xs"],
                color: tokens.text.muted,
                mt: 0.25,
              }}
            >
              {slot.description}
            </Typography>
          </Box>
        </Stack>

        <Button
          size="small"
          variant="outlined"
          startIcon={
            uploading && uploadTargetSlotId === slot.slotId
              ? <CircularProgress size={14} />
              : <UploadIcon sx={{ fontSize: 16 }} />
          }
          onClick={() => onUploadClick(sectionId, slot.slotId)}
          disabled={uploading}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.semibold,
            color: tokens.text.primary,
            borderColor: tokens.border.default,
            borderRadius: tokens.radius.sm,
            px: 2,
            py: 0.5,
            minWidth: 90,
            flexShrink: 0,
            "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.text.muted },
          }}
        >
          {hasDoc && slot.allowMultiple ? "Add More" : hasDoc ? "Replace" : "Upload"}
        </Button>
        {existingRequestSlotIds.has(slot.slotId) ? (
          <Chip
            label="Requested"
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: tokens.fontSize["2xs"],
              color: tokens.status.warning,
              borderColor: `${tokens.status.warning}40`,
            }}
          />
        ) : pendingSlotIds.has(slot.slotId) ? (
          <Chip
            label="In queue"
            size="small"
            sx={{
              height: 22,
              fontSize: tokens.fontSize["2xs"],
              bgcolor: `${tokens.brand.primary}12`,
              color: tokens.brand.primary,
            }}
          />
        ) : (
          <Button
            size="small"
            variant="text"
            onClick={() => onRequestRevision(slot.slotId, slot.label)}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize["2xs"],
              color: tokens.status.warning,
              minWidth: 0,
              px: 1,
              "&:hover": { bgcolor: `${tokens.status.warning}08` },
            }}
          >
            Request
          </Button>
        )}
      </Stack>

      {/* Uploaded Files */}
      {slotDocs.map((doc: any) => (
        <DocumentFileCard
          key={doc._id}
          doc={doc}
          onView={onViewDoc}
          onDownload={onDownloadDoc}
          onDelete={onDeleteDoc}
        />
      ))}

      {/* Per-document AI validation checklist */}
      {hasDoc && checklistData && (
        <InlineChecklist
          data={checklistData}
          reasoning={checklistData.reasoning}
          citations={checklistData.citations}
          checkId={checklistData.checkId}
          onItemClick={onChecklistItemClick}
          onRerun={onRerun}
          rerunning={rerunningCheckId === checklistData.checkId}
        />
      )}

      {/* Show existing revision request for this slot */}
      {existingRequests
        .filter((r: any) => r.slotId === slot.slotId && r.status === "pending")
        .map((req: any) => (
          <Box
            key={req._id}
            sx={{
              ml: 5,
              mr: 2,
              mb: 1,
              mt: 0.5,
              border: `1px solid ${tokens.status.warning}30`,
              borderLeft: `3px solid ${tokens.status.warning}`,
              borderRadius: tokens.radius.sm,
              bgcolor: `${tokens.status.warning}06`,
              px: 1.5,
              py: 1,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Chip
                  label="Revision Requested"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 9,
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: `${tokens.status.warning}15`,
                    color: tokens.status.warning,
                  }}
                />
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.disabled }}>
                  by {req.requestedBy}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Tooltip title="Edit request">
                  <IconButton
                    size="small"
                    onClick={() => onEditRequest(req)}
                    sx={{
                      color: tokens.text.muted,
                      p: 0.5,
                      "&:hover": { color: tokens.brand.primary, bgcolor: `${tokens.brand.primary}08` },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete request">
                  <IconButton
                    size="small"
                    onClick={() => onDeleteRequest(req._id)}
                    sx={{
                      color: tokens.text.muted,
                      p: 0.5,
                      "&:hover": { color: tokens.status.danger, bgcolor: `${tokens.status.danger}08` },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onRevisionUploadClick(
                    req._id,
                    slot.category || sectionId,
                    slot.slotId,
                    req.reason
                  )}
                  sx={{
                    textTransform: "none",
                    fontSize: tokens.fontSize["2xs"],
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: tokens.status.warning,
                    borderRadius: tokens.radius.sm,
                    boxShadow: "none",
                    px: 1.5,
                    py: 0.25,
                    minHeight: 26,
                    "&:hover": { bgcolor: "#b45309", boxShadow: "none" },
                  }}
                >
                  Upload Revision
                </Button>
              </Stack>
            </Stack>
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body, lineHeight: 1.5, mt: 0.5 }}>
              {req.reason}
            </Typography>
            {req.requirements?.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {req.requirements.map((r: string, i: number) => (
                  <Typography key={i} sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, pl: 0.5 }}>
                    {"\u2022"} {r}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ))}
    </Box>
  );
};
